import { useQuery } from '@tanstack/react-query';
import { useEnabledQuery } from '../../../ui/hooks/useEnabledQuery';
import { ResourceType } from '../../enumerations/ResourceType';
import { get } from '../APIVersion1';
import { IdSchema } from '../baseSchemas';
import { ParentedPersistableResource, PersistableSchemaType } from '../persistableResourceTypes';
import { useResourceAdapterActions } from './useResourceAdapterActions';

export function useMultiResourceSingleParentedResourceAdapterBaseActions<R extends ParentedPersistableResource>(
    apiPostfix: string,
    resourceType: ResourceType,
    resourceSchema: PersistableSchemaType<R>
) {
    const actions = useResourceAdapterActions(apiPostfix, resourceType, resourceSchema);
    const getEnabled = useEnabledQuery();

    const useGetAllResources = (parentId: string | undefined | null) => {
        return useQuery({
            queryKey: [parentId, resourceType],
            queryFn: () => actions.getAllResourcesWithParent(parentId as string),
            enabled: getEnabled(parentId),
        });
    };

    const useGetUnusedIds = (parentId: string) => {
        return useQuery({
            queryKey: ['UNUSED', resourceType, parentId],
            queryFn: () =>
                get(`${apiPostfix}/unused-ids?data-dictionary-version-id=${parentId}`).then((result) =>
                    actions.processGetResponse(result, [] as string[], IdSchema.array())
                ),
            enabled: getEnabled(parentId),
        });
    };

    return {
        batchAddResource: actions.batchAddResource,
        addOrSetResource: actions.addOrSetResource,
        addResourceToIndex: actions.addResourceToIndex,
        getSingleFromParent: actions.getSingleFromParent,
        getAllResourcesWithParent: actions.getAllResourcesWithParent,
        changeIndexOfResource: actions.changeIndexOfResource,
        getAllResources: actions.getAllResources,

        useGetDependencyValidation: actions.useGetDependencyValidation,
        useGetUnusedIds,
        useValidateResource: actions.useValidateResource,
        useValidateAllResourcesSingleParented: actions.useValidateAllResourcesSingleParented,
        useGetResource: actions.useGetResource,
        useUpdateResource: actions.useUpdateResource,
        useDeleteResource: actions.useDeleteResource,
        processMutationResponse: actions.processMutationResponse,
        processGetResponse: actions.processGetResponse,
        processGetCollectionResponse: actions.processGetCollectionResponse,
        processGetResourceResponse: actions.processGetResourceResponse,
        useGetAllResources,
    };
}
